'use client';

import { cn } from '@/shadcn/lib/utils';
import { Input } from '@/shared/components/ui/Input';
import { RangeSlider } from '@/shared/components/ui/RangeSlider';
import { CHIP_BASE, CHIP_ON, CHIP_OFF, CHIP_QUIET } from '@/shared/components/ui/chip';
import type { QuickRange } from '../../constants/filterConstants';

interface Props {
    min: number;
    max: number;
    step: number;
    from: number;
    to: number;
    onChange: (from: number, to: number) => void;
    format: (n: number | undefined | null) => string;
    /** Rychle presety pod sliderom. */
    quickRanges?: QuickRange[];
    /** Presne ciselne polia pod sliderom (rozsireny dialog). */
    withInputs?: boolean;
    inputIcon?: React.ReactNode;
    labelFrom: string;
    labelTo: string;
    /** Text pre horny okraj, ked je na maxime ("bez limitu"). */
    unlimitedLabel?: string;
    className?: string | string[];
}

/**
 * Rozsahove pole: dvojuchytovy slider + rychle presety a volitelne presne
 * ciselne polia. Pouziva sa pre cenu aj plochu, v hero aj v rozsirenom dialogu.
 */
export function RangeField(props: Props) {
    const { min, max, step, from, to, onChange } = props;
    const atFrom = from <= min;
    const atTo = to >= max;
    const unlimited = props.unlimitedLabel ?? 'bez limitu';

    /* Prazdny input = hranica (needitovane pole ukazuje placeholder). */
    const inputValue = (v: number, bound: number) => (v === bound ? '' : v);
    const clamp = (n: number) => Math.min(max, Math.max(min, n));

    return (
        <div className={cn('w-full', props.className)}>
            {/* citanie aktualneho rozsahu */}
            <div className="flex items-center justify-between px-[12px] text-[13px] font-[600]">
                <span>{props.format(from)}</span>
                <span className="text-black/40">–</span>
                <span>{atTo ? unlimited : props.format(to)}</span>
            </div>

            <RangeSlider
                min={min}
                max={max}
                step={step}
                valueFrom={from}
                valueTo={to}
                format={props.format}
                ariaLabelFrom={props.labelFrom}
                ariaLabelTo={props.labelTo}
                onChange={onChange}
            />

            {props.withInputs && (
                <div className="flex items-center gap-[10px] px-[10px] pt-[4px] sm:gap-[15px]">
                    <Input
                        numeric
                        leftIcon={props.inputIcon}
                        disableFocusStyles
                        variant="rounded"
                        placeholder={props.labelFrom}
                        value={inputValue(from, min)}
                        onChange={(e) =>
                            onChange(e.target.value === '' ? min : Number(e.target.value), to)
                        }
                        // dorovnanie az po opusteni pola - pocas pisania nekazi vstup
                        onBlur={() => onChange(Math.min(clamp(from), to), to)}
                    />
                    <Input
                        numeric
                        leftIcon={props.inputIcon}
                        disableFocusStyles
                        variant="rounded"
                        placeholder={props.labelTo}
                        value={inputValue(to, max)}
                        onChange={(e) =>
                            onChange(from, e.target.value === '' ? max : Number(e.target.value))
                        }
                        onBlur={() => onChange(from, Math.max(clamp(to), from))}
                    />
                </div>
            )}

            {props.quickRanges && props.quickRanges.length > 0 && (
                <div className="flex flex-wrap gap-[6px] px-[10px] pt-[8px]">
                    {props.quickRanges.map((q) => {
                        const qFrom = Math.max(min, q.from ?? min);
                        const qTo = Math.min(max, q.to ?? max);
                        if (qFrom >= qTo) return null;
                        const active = from === qFrom && to === qTo;
                        return (
                            <button
                                key={q.label}
                                type="button"
                                className={cn(CHIP_BASE, active ? CHIP_ON : CHIP_OFF)}
                                onClick={() => onChange(qFrom, qTo)}
                            >
                                {q.label}
                            </button>
                        );
                    })}
                    <button
                        type="button"
                        className={cn(CHIP_BASE, CHIP_QUIET)}
                        onClick={() => onChange(min, max)}
                        disabled={atFrom && atTo}
                    >
                        Bez limitu
                    </button>
                </div>
            )}
        </div>
    );
}
